/**
 * Utility functions for working with the MUI theme system
 */

import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Common box shadow styles using theme values
 */
export const boxShadows = {
  heavy: '0 8px 24px rgba(42, 32, 24, 0.1)',
  light: '0 2px 12px rgba(42, 32, 24, 0.06)',
  medium: '0 4px 16px rgba(42, 32, 24, 0.08)',
  none: 'none',
};

/**
 * Common border radius values
 */
export const borderRadius = {
  large: 100,
  medium: 12,
  round: '50%',
  small: 8,
};

/**
 * Layout constants for consistent spacing
 */
export const layout = {
  navbarHeight: 64,
} as const;

/**
 * Helper to create consistent card styles
 */
export const cardStyles: SxProps<Theme> = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: borderRadius.medium,
  boxShadow: boxShadows.light,
  p: 3,
};

/**
 * Helper to create consistent modal styles
 */
export const modalStyles: SxProps<Theme> = {
  bgcolor: 'background.paper',
  borderRadius: borderRadius.medium,
  boxShadow: boxShadows.heavy,
  left: '50%',
  maxHeight: '90vh',
  overflow: 'auto',
  p: 4,
  position: 'absolute' as const,
  top: '50%',
  transform: 'translate(-50%, -50%)',
};

/**
 * Helper to create responsive container padding
 */
export const containerPadding: SxProps<Theme> = {
  px: {
    lg: 6,
    md: 4,
    sm: 3,
    xs: 2,
  },
  py: {
    md: 4,
    sm: 3,
    xs: 2,
  },
};

/**
 * Helper to create consistent button styles
 */
export const buttonStyles = {
  outlined: {
    '&:hover': {
      bgcolor: 'primary.light',
      borderColor: 'primary.dark',
    },
    borderColor: 'primary.main',
    color: 'primary.main',
  },
  primary: {
    '&:hover': {
      bgcolor: 'primary.dark',
    },
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
  },
  secondary: {
    '&:hover': {
      bgcolor: 'secondary.dark',
    },
    bgcolor: 'secondary.main',
    color: 'secondary.contrastText',
  },
} as const;

/**
 * Helper to create truncated text with ellipsis
 * @param lines - Number of lines before truncation
 * @returns SxProps for text truncation
 */
export const truncateText = (lines = 1): SxProps<Theme> => {
  return {
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lines,
  };
};

/**
 * Helper to create flex center alignment
 */
export const flexCenter: SxProps<Theme> = {
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
};

/**
 * Helper to create flex column layout
 */
export const flexColumn: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Helper to create absolute positioning
 */
export const absoluteFill: SxProps<Theme> = {
  bottom: 0,
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
};

/**
 * Transition durations from theme
 */
export const transitions = {
  fast: '150ms',
  normal: '300ms',
  slow: '500ms',
};

/**
 * Helper to create consistent input styles
 */
export const inputStyles: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: 'primary.main',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
    },
    bgcolor: 'background.paper',
    borderRadius: borderRadius.small,
  },
};
